import { useState, useRef, useEffect } from 'react'
import { Search, X, Bot, MessageSquare, Settings, FileText } from 'lucide-react'

const SearchBar = () => {
  const [query, setQuery] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const inputRef = useRef(null)

  const suggestions = [
    {
      icon: Bot,
      title: 'Create new chatbot',
      description: 'Set up a new AI chatbot for your business',
      category: 'Actions'
    },
    {
      icon: MessageSquare,
      title: 'Chat history',
      description: 'View your recent conversations',
      category: 'Navigation'
    },
    {
      icon: Settings,
      title: 'Bot settings',
      description: 'Configure your chatbot preferences',
      category: 'Settings'
    },
    {
      icon: FileText,
      title: 'Analytics report',
      description: 'View performance metrics and insights',
      category: 'Reports'
    }
  ]

  const handleInputChange = (e) => {
    setQuery(e.target.value)
    setShowSuggestions(e.target.value.length > 0)
  }

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.title)
    setShowSuggestions(false)
    // Handle navigation or action based on suggestion
    console.log('Selected:', suggestion.title)
  }

  const handleClear = () => {
    setQuery('')
    setShowSuggestions(false)
    inputRef.current?.focus()
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false)
      inputRef.current?.blur()
    }
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false)
        setIsFocused(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative w-full" ref={inputRef}>
      {/* Search Input */}
      <div className={`relative ${isFocused ? 'ring-2 ring-primary-500/20' : ''}`}>
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="w-5 h-5 text-secondary-400" />
        </div>
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            setIsFocused(true)
            if (query.length > 0) setShowSuggestions(true)
          }}
          onKeyDown={handleKeyDown}
          placeholder="Search chatbots, settings, or ask AI..."
          className="w-full pl-10 pr-10 py-3 bg-secondary-800/50 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all duration-300"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <X className="w-5 h-5 text-secondary-400 hover:text-white transition-colors" />
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-secondary-800 border border-secondary-700 rounded-xl shadow-xl z-50 max-h-80 overflow-y-auto">
          <div className="p-2">
            {suggestions
              .filter(suggestion => 
                suggestion.title.toLowerCase().includes(query.toLowerCase()) ||
                suggestion.description.toLowerCase().includes(query.toLowerCase())
              )
              .map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary-700 transition-colors text-left group"
                >
                  <div className="flex-shrink-0">
                    <suggestion.icon className="w-5 h-5 text-secondary-400 group-hover:text-primary-400 transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium text-sm">
                      {suggestion.title}
                    </div>
                    <div className="text-secondary-400 text-xs mt-1">
                      {suggestion.description}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="text-xs text-secondary-500 bg-secondary-700 px-2 py-1 rounded">
                      {suggestion.category}
                    </span>
                  </div>
                </button>
              ))}
            
            {query && (
              <div className="border-t border-secondary-700 mt-2 pt-2">
                <button className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary-700 transition-colors text-left group">
                  <Search className="w-5 h-5 text-secondary-400 group-hover:text-primary-400 transition-colors" />
                  <div className="flex-1">
                    <div className="text-white font-medium text-sm">
                      Search for "{query}"
                    </div>
                    <div className="text-secondary-400 text-xs mt-1">
                      Find chatbots, conversations, and settings
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default SearchBar
